import React from 'react';

const ColorPicker = ({ color, onChange }) => {
    // Convert hex to HSL
    const hexToHSL = (hex) => {
        // Remove the hash if present
        hex = hex.replace(/^#/, '');

        // Parse the hex values
        let r = parseInt(hex.slice(0, 2), 16) / 255;
        let g = parseInt(hex.slice(2, 4), 16) / 255;
        let b = parseInt(hex.slice(4, 6), 16) / 255;

        let max = Math.max(r, g, b);
        let min = Math.min(r, g, b);
        let h, s, l = (max + min) / 2;
        let d = max - min;

        if (d === 0) {
            h = s = 0;
        } else {
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
            
            switch (max) {
                case r:
                    h = (g - b) / d + (g < b ? 6 : 0);
                    break;
                case g:
                    h = (b - r) / d + 2;
                    break;
                case b:
                    h = (r - g) / d + 4;
                    break;
                default:
                    break;
            }
            h /= 6;
        }

        return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
    };

    // Convert HSL to hex
    const hslToHex = (h, s, l) => {
        h /= 360;
        s /= 100;
        l /= 100;

        let r, g, b;

        if (s === 0) {
            r = g = b = l;
        } else {
            const hue2rgb = (p, q, t) => {
                if (t < 0) t += 1;
                if (t > 1) t -= 1;
                if (t < 1/6) return p + (q - p) * 6 * t;
                if (t < 1/2) return q;
                if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
                return p;
            };

            const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
            const p = 2 * l - q;

            r = hue2rgb(p, q, h + 1/3);
            g = hue2rgb(p, q, h);
            b = hue2rgb(p, q, h - 1/3);
        }

        const toHex = x => {
            const hex = Math.round(x * 255).toString(16);
            return hex.length === 1 ? '0' + hex : hex;
        };

        return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    };

    const [h] = hexToHSL(color);

    // Generate shade variations
    const generateShades = (hue) => {
        return [
            hslToHex(hue, 70, 30),  // Darkest
            hslToHex(hue, 70, 45),  // Darker
            hslToHex(hue, 70, 60),  // Medium
            hslToHex(hue, 70, 75),  // Lighter
            hslToHex(hue, 70, 90),  // Lightest
        ];
    };

    return (
        <div className="w-full max-w-md space-y-4">
            {/* Hue Slider */}
            <input
                type="range"
                min="0"
                max="359"
                value={h}
                onChange={(e) => {
                    const newHue = parseInt(e.target.value);
                    onChange(hslToHex(newHue, 70, 54));
                }}
                className="w-full h-8 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-8 [&::-webkit-slider-thumb]:w-8 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-gray-300 [&::-webkit-slider-thumb]:shadow-lg"

                style={{
                    background: `linear-gradient(to right,
                        ${hslToHex(0, 70, 54)},   /* Red */
                        ${hslToHex(60, 70, 54)},  /* Yellow */
                        ${hslToHex(120, 70, 54)}, /* Green */
                        ${hslToHex(180, 70, 54)}, /* Cyan */
                        ${hslToHex(240, 70, 54)}, /* Blue */
                        ${hslToHex(300, 70, 54)}, /* Magenta */
                        ${hslToHex(359, 70, 54)}  /* Red (full circle) */
                    )`
                }}
            />
            
            {/* Shade Swatches */}
            <div className="flex justify-between gap-2">
                {generateShades(h).map((shade, index) => (
                    <button
                        type="button"
                        key={index}
                        onClick={() => onChange(shade)}
                        className={`w-full aspect-square rounded-lg border-2 transition-transform hover:scale-105 ${
                            color.toLowerCase() === shade.toLowerCase() 
                                ? 'border-gray-400' 
                                : 'border-transparent'
                        }`}
                        style={{ backgroundColor: shade }}
                    />
                ))}
            </div>
        </div>
    );
};

export default ColorPicker;
